import { isTuningHistogram, tuningHistogramBucketIndex } from '../contracts/measurements';
import type { TuningHistogram } from '../contracts/measurements';

/** Constant-cost observation and lossless aggregation of one timing source/segment. */
export class GuestHistogramReducer {
  private readonly counts = new Map<number, number>();
  private count = 0;
  private maximum: number | null = null;
  private startMs: number | undefined;
  private endMs = 0;

  constructor(private readonly source: string) {}
  observe(valueMs: number, monotonicMs: number): void {
    if (!Number.isFinite(valueMs) || valueMs < 0 || !Number.isFinite(monotonicMs) || monotonicMs < 0) return;
    if (this.startMs !== undefined && monotonicMs < this.endMs) this.reset();
    const index = tuningHistogramBucketIndex(valueMs);
    this.counts.set(index, (this.counts.get(index) ?? 0) + 1);
    this.count++; this.maximum = Math.max(this.maximum ?? 0, valueMs);
    this.startMs ??= monotonicMs; this.endMs = monotonicMs;
  }
  merge(histogram: TuningHistogram): boolean {
    if (!isTuningHistogram(histogram) || histogram.source !== this.source || histogram.availability !== 'measured' ||
      (this.startMs !== undefined && histogram.monotonicStartMs < this.endMs)) return false;
    for (const [index, count] of histogram.buckets) this.counts.set(index, (this.counts.get(index) ?? 0) + count);
    this.count += histogram.sampleCount;
    this.maximum = histogram.maxMs === null ? this.maximum : Math.max(this.maximum ?? 0, histogram.maxMs);
    this.startMs ??= histogram.monotonicStartMs; this.endMs = histogram.monotonicEndMs;
    return true;
  }
  snapshot(interval?: { startMs: number; endMs: number }): TuningHistogram {
    const represented = interval && interval.startMs >= 0 && interval.startMs <= (this.startMs ?? interval.startMs) &&
      interval.endMs >= this.endMs && interval.endMs >= interval.startMs ? interval : undefined;
    return { schema: 'ms-v1', unit: 'ms', source: this.source,
      availability: this.count > 0 ? 'measured' : 'not-sampled',
      monotonicStartMs: represented?.startMs ?? this.startMs ?? this.endMs,
      monotonicEndMs: represented?.endMs ?? this.endMs,
      buckets: [...this.counts].sort(([a], [b]) => a - b), sampleCount: this.count, maxMs: this.maximum };
  }
  flush(interval?: { startMs: number; endMs: number }): TuningHistogram {
    const histogram = this.snapshot(interval); this.reset(); return histogram;
  }
  reset(): void { this.counts.clear(); this.count = 0; this.maximum = null; this.startMs = undefined; this.endMs = 0; }
}
